<script setup lang="ts">
  import type { PropType } from 'vue'

  import { computed, ref, onMounted } from 'vue'

  import { useI18n } from 'vue-i18n'

  import { useRoute, useRouter } from 'nuxt/app'

  import CPText from '@ui/components/CPText.vue'

  import AuthBrandingHeader from '../AuthBrandingHeader.vue'

  import { useAuth } from '../../composables/useAuth'

  import { LoginEvents } from '../../types/auth'

  import { MigrationUtility } from '../../utils/migration'

  import SetPassword from './SetPassword.vue'

  import type { AuthResponse, MigrationSessionData } from '../../types'

  defineProps({
    showBranding: {
      type: Boolean,
      default: false,
    },
    portalLogo: {
      type: String as PropType<string | null>,
      default: null,
    },
    portalName: {
      type: String,
      default: 'ClientPortal',
    },
  })

  const { t } = useI18n()

  const router = useRouter()
  const route = useRoute()

  const auth = useAuth()

  const migrationData = ref<MigrationSessionData | null>(null)

  const email = computed(() => migrationData.value?.email || '')

  const contactId = computed(() => migrationData.value?.contactId || auth.store.contactId || '')

  const passwordResetToken = computed(() => migrationData.value?.passwordResetToken || '')

  const isPreLogin = computed(() => migrationData.value?.preLogin === true)

  // One-time credentials that arrive on `/auth` (`token` / `loginCode` /
  // `sessionKey` / `email`) get spread into `/expired-password` by the
  // migration gate. They MUST be stripped before forwarding to
  // `/profile-confirmation` so they do not remain in the address bar /
  // browser history after sign-in. Only `redirect` (and other non-credential
  // params) should survive — `useAuthPage.handleLoginCallback` reads
  // `route.query.redirect` to resume the user's originally requested page.
  const AUTH_CREDENTIAL_KEYS = new Set(['token', 'loginCode', 'sessionKey', 'email'])

  const onPasswordSet = async (event: { eventType?: string } & Partial<AuthResponse>) => {
    if (event?.eventType !== LoginEvents.LOGIN_SUCCESSFUL) return

    if (isPreLogin.value && event.token) {
      await auth.saveAuthAfterLogin(event as AuthResponse)
      return
    }

    const safeQuery: Record<string, string> = {}
    for (const [key, value] of Object.entries(route.query)) {
      if (AUTH_CREDENTIAL_KEYS.has(key)) continue
      if (typeof value === 'string') safeQuery[key] = value
      else if (Array.isArray(value)) {
        const first = value.find(v => typeof v === 'string') as string | undefined
        if (first) safeQuery[key] = first
      }
    }

    await router.replace({ path: '/profile-confirmation', query: safeQuery })
  }

  onMounted(() => {
    migrationData.value = MigrationUtility.get()
  })
</script>

<template>
  <div class="w-full">
    <AuthBrandingHeader :show-branding="showBranding" :portal-logo="portalLogo" :portal-name="portalName" />

    <div class="mb-4 w-full">
      <CPText size="md">{{ t('auth.expiredPassword.description') }}</CPText>
    </div>

    <SetPassword :email="email" :contact-id="contactId" :password-reset-token="passwordResetToken" @loginCallback="onPasswordSet" />
  </div>
</template>
